import { Box, CircularProgress } from "@mui/material"
import { createFileRoute, redirect } from "@tanstack/react-router"

import { useAuthStore } from "@/stores/authStore"
import theme from "@/styles/mui-theme"
import { exchangeCodeForTokens, getAuthCodeFromUrl } from "@/utils/auth/auth"
import { exchangeTokenWithAPI } from "@/utils/auth/exchangeToken"

export const Route = createFileRoute("/auth/callback")({
  beforeLoad: async () => {
    // Get authorization code from URL query params (authorization code flow)
    const authCode = getAuthCodeFromUrl()
    if (!authCode) {
      useAuthStore.getState().signIn()
      return null
    }

    const store = useAuthStore.getState()
    try {
      store.setStatus("loading")

      // Exchange authorization code for Cognito tokens (including refresh token)
      const cognitoTokens = await exchangeCodeForTokens(authCode)

      // Exchange ID token + Cognito refresh token for backend tokens
      const backendTokens = await exchangeTokenWithAPI(cognitoTokens.id_token, cognitoTokens.refresh_token)

      // Set all tokens at once to prevent multiple state updates
      store.setTokens({
        idToken: cognitoTokens.id_token,
        accessToken: backendTokens.accessToken,
        refreshToken: backendTokens.refreshToken,
      })

      // Set authenticated status
      store.setStatus("authenticated")

      // Get the redirect URL from the state parameter in the URL query params
      const params = new URLSearchParams(window.location.search)
      const redirectUrl = params.get("state") || "/"

      return redirect({ to: redirectUrl, replace: true })
    } catch (error) {
      console.error("Auth callback failed:", error)
      await store.clearAuth() // This sets status to unauthenticated
      store.signIn()
      return null
    }
  },
  component: AuthCallback,
})

function AuthCallback() {
  const { status } = useAuthStore()

  if (status === "loading") {
    return (
      <Box className='flex h-screen items-center justify-center'>
        <CircularProgress
          size={48}
          className='transition-opacity duration-300 ease-in-out'
          sx={{
            color: theme.palette.primary.main,
          }}
        />
      </Box>
    )
  }

  return null
}
